import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChartBar, Gear, UsersThree, Scales, ChartPieSlice, Warning, ArrowLeft, Clock, Trophy,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdmin } from "@/lib/permissions";
import { LOGO_URL, num, rupee, rupeePdf, pct, inRange, lastNDates, lastNMonths, monthLabel, CHART_COLORS, exportCsv, exportPdf, captureCharts } from "@/components/reports/reportUtils";
import SortableTable, { ReportToolbar } from "@/components/reports/SortableTable";
import IdleTimeReport from "@/components/reports/IdleTimeReport";
import OperatorEfficiency from "@/components/reports/OperatorEfficiency";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const useList = (entity) => useQuery({ queryKey: ["entity", entity], queryFn: async () => (await api.get(`/${entity}`)).data }).data || [];
const QC = new Set(["QC", "QC/Inspection"]);

function FilterField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <Label className="text-[11px] uppercase tracking-wide text-slate-400">{label}</Label>
      {children}
    </div>
  );
}
function DateRange({ from, to, set }) {
  return (
    <>
      <FilterField label="From"><Input type="date" value={from} onChange={(e) => set("from", e.target.value)} className="h-9 w-36" data-testid="filter-from" /></FilterField>
      <FilterField label="To"><Input type="date" value={to} onChange={(e) => set("to", e.target.value)} className="h-9 w-36" data-testid="filter-to" /></FilterField>
    </>
  );
}
function StatCard({ label, value, sub, tone = "slate" }) {
  const tones = { slate: "text-slate-900", green: "text-emerald-600", red: "text-red-600" };
  return (
    <Card className="p-4 border-slate-200 shadow-none">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-lg font-bold mt-1 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5 truncate">{sub}</div>}
    </Card>
  );
}
const ChartCard = ({ title, children }) => (
  <Card className="p-5 border-slate-200 shadow-none mt-6" data-chart-capture data-chart-title={title}>
    <h3 className="font-semibold text-slate-900 mb-4">{title}</h3>
    {children}
  </Card>
);

/* ---------------- REPORT 1: JOB PROFITABILITY ---------------- */
function JobProfitability({ jobs, costs, external }) {
  const rootRef = useRef(null);
  const [f, setF] = useState({ from: "", to: "", status: "All", client: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const costByJob = useMemo(() => { const m = {}; costs.forEach((c) => m[c.job_id] = (m[c.job_id] || 0) + num(c.actual_cost)); return m; }, [costs]);
  const extByJob = useMemo(() => { const m = {}; external.forEach((e) => m[e.job_id] = (m[e.job_id] || 0) + num(e.actual_cost)); return m; }, [external]);

  const rows = useMemo(() => jobs
    .filter((j) => inRange(j.start_date, f.from, f.to))
    .filter((j) => f.status === "All" || j.job_status === f.status)
    .filter((j) => !f.client || (j.client_name || "").toLowerCase().includes(f.client.toLowerCase()))
    .map((j) => {
      const actual = (costByJob[j.id] || 0) + (extByJob[j.id] || 0);
      const alloc = num(j.budget_allocated);
      const profit = alloc - actual;
      const ppct = alloc ? (profit / alloc) * 100 : 0;
      return { ...j, actual, profit, ppct };
    }), [jobs, costByJob, extByJob, f]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    const most = [...rows].sort((a, b) => b.ppct - a.ppct)[0];
    const least = [...rows].sort((a, b) => a.ppct - b.ppct)[0];
    const avg = rows.reduce((s, r) => s + r.ppct, 0) / rows.length;
    return { most, least, avg };
  }, [rows]);

  const columns = [
    { key: "code", label: "Job ID", value: (r) => r.code, render: (r) => <span className="font-mono font-medium">{r.code}</span> },
    { key: "client_name", label: "Client" },
    { key: "job_name", label: "Job Name" },
    { key: "product_type", label: "Type" },
    { key: "budget_allocated", label: "Budget", align: "right", sortValue: (r) => num(r.budget_allocated), value: (r) => rupee(r.budget_allocated), pdf: (r) => rupeePdf(r.budget_allocated), csv: (r) => num(r.budget_allocated) },
    { key: "actual", label: "Actual Cost", align: "right", sortValue: (r) => r.actual, value: (r) => rupee(r.actual), pdf: (r) => rupeePdf(r.actual), csv: (r) => r.actual },
    { key: "profit", label: "Profit / Loss", align: "right", sortValue: (r) => r.profit, render: (r) => <span className={r.profit >= 0 ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>{rupee(r.profit)}</span>, pdf: (r) => rupeePdf(r.profit), csv: (r) => r.profit },
    { key: "ppct", label: "Profitability %", align: "right", sortValue: (r) => r.ppct, render: (r) => <span className={r.ppct >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>{pct(r.ppct)}</span>, pdf: (r) => pct(r.ppct), csv: (r) => r.ppct.toFixed(1) },
  ];

  return (
    <div ref={rootRef}>
      <ReportToolbar title="Job Profitability Report" subtitle="Budget vs actual cost (incl. external processes)"
        onCsv={() => exportCsv("job_profitability", columns, rows)}
        onPdf={async () => exportPdf({ title: "Job Profitability", subtitle: `${rows.length} jobs`, columns, rows, filename: "job_profitability", charts: await captureCharts(rootRef.current) })}>
        <DateRange from={f.from} to={f.to} set={set} />
        <FilterField label="Status">
          <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger className="h-9 w-36" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>{["All", "Draft", "Active", "Completed", "Delivered"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        </FilterField>
        <FilterField label="Client"><Input value={f.client} onChange={(e) => set("client", e.target.value)} placeholder="Search..." className="h-9 w-40" data-testid="filter-client" /></FilterField>
      </ReportToolbar>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Most Profitable Job" value={pct(stats.most.ppct)} sub={`${stats.most.code} · ${stats.most.job_name}`} tone="green" />
          <StatCard label="Least Profitable Job" value={pct(stats.least.ppct)} sub={`${stats.least.code} · ${stats.least.job_name}`} tone="red" />
          <StatCard label="Average Profitability %" value={pct(stats.avg)} />
        </div>
      )}
      <SortableTable columns={columns} rows={rows} initialSort={{ key: "ppct", dir: "desc" }} testId="r1" />

      <ChartCard title="Budget vs Actual Cost by Job">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={[...rows].sort((a, b) => num(b.budget_allocated) - num(a.budget_allocated)).slice(0, 15).map((r) => ({ name: r.code, Budget: num(r.budget_allocated), Actual: r.actual }))} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={0} angle={-20} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
            <Tooltip formatter={(v) => rupee(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Budget" fill="#2563eb" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Actual" fill="#dc2626" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

/* ---------------- REPORT 2: MACHINE UTILIZATION ---------------- */
function MachineUtilization({ machines, utilization }) {
  const rootRef = useRef(null);
  const [f, setF] = useState({ from: "", to: "", type: "All", category: "All" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const types = ["All", ...new Set(machines.map((m) => m.machine_type).filter(Boolean))];
  const cats = ["All", ...new Set(machines.map((m) => m.category).filter(Boolean))];

  const build = (mlist) => mlist
    .filter((m) => f.type === "All" || m.machine_type === f.type)
    .filter((m) => f.category === "All" || m.category === f.category)
    .map((m) => {
      const ents = utilization.filter((u) => u.machine_id === m.id && inRange(u.date, f.from, f.to));
      const planned = ents.reduce((s, u) => s + num(u.planned_hours), 0);
      const actual = ents.reduce((s, u) => s + num(u.actual_hours), 0);
      const idle = ents.reduce((s, u) => s + num(u.idle_hours), 0);
      const maint = ents.reduce((s, u) => s + num(u.maintenance_hours), 0);
      const u = planned ? (actual / planned) * 100 : 0;
      const cls = u < 60 ? "bg-red-50" : u > 95 ? "bg-orange-50" : "";
      return { ...m, planned, actual, idle, maint, util: u, __rowClass: cls };
    });

  const prodRows = build(machines.filter((m) => !QC.has(m.category)));
  const qcRows = build(machines.filter((m) => QC.has(m.category)));

  const columns = [
    { key: "code", label: "Machine ID", render: (r) => <span className="font-mono font-medium">{r.code}</span> },
    { key: "machine_name", label: "Name" },
    { key: "machine_type", label: "Type" },
    { key: "category", label: "Category" },
    { key: "planned", label: "Planned Hrs", align: "right", value: (r) => r.planned.toFixed(1) },
    { key: "actual", label: "Actual Hrs", align: "right", value: (r) => r.actual.toFixed(1) },
    { key: "util", label: "Utilization %", align: "right", sortValue: (r) => r.util, render: (r) => <Badge variant="outline" className={r.util < 60 ? "bg-red-100 text-red-700 border-red-200" : r.util > 95 ? "bg-orange-100 text-orange-700 border-orange-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>{pct(r.util)}</Badge>, pdf: (r) => pct(r.util), csv: (r) => r.util.toFixed(1) },
    { key: "idle", label: "Idle Hrs", align: "right", value: (r) => r.idle.toFixed(1) },
    { key: "maint", label: "Maint. Hrs", align: "right", value: (r) => r.maint.toFixed(1) },
  ];

  // 30-day trend per machine
  const days = lastNDates(30);
  const trend = days.map((d) => {
    const row = { date: d.slice(5) };
    prodRows.concat(qcRows).forEach((m) => {
      const ents = utilization.filter((u) => u.machine_id === m.id && u.date === d);
      const p = ents.reduce((s, u) => s + num(u.planned_hours), 0);
      const a = ents.reduce((s, u) => s + num(u.actual_hours), 0);
      row[m.code] = p ? Math.round((a / p) * 100) : 0;
    });
    return row;
  });
  const trendMachines = prodRows.concat(qcRows);

  return (
    <div ref={rootRef}>
      <ReportToolbar title="Machine Utilization Report" subtitle="Under 60% = red (underused), over 95% = orange (bottleneck)"
        onCsv={() => exportCsv("machine_utilization", columns, prodRows.concat(qcRows))}
        onPdf={async () => exportPdf({ title: "Machine Utilization", subtitle: `${prodRows.length + qcRows.length} machines`, columns, rows: prodRows.concat(qcRows), filename: "machine_utilization", charts: await captureCharts(rootRef.current) })}>
        <DateRange from={f.from} to={f.to} set={set} />
        <FilterField label="Type"><Select value={f.type} onValueChange={(v) => set("type", v)}><SelectTrigger className="h-9 w-40" data-testid="filter-type"><SelectValue /></SelectTrigger><SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></FilterField>
        <FilterField label="Category"><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger className="h-9 w-40" data-testid="filter-category"><SelectValue /></SelectTrigger><SelectContent>{cats.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></FilterField>
      </ReportToolbar>

      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Production Machines</div>
      <SortableTable columns={columns} rows={prodRows} initialSort={{ key: "util", dir: "desc" }} testId="r2-prod" />
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mt-6 mb-2">QC / Inspection Equipment</div>
      <SortableTable columns={columns} rows={qcRows} initialSort={{ key: "util", dir: "desc" }} testId="r2-qc" />

      <ChartCard title="Utilization % Trend — Last 30 Days">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={4} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {trendMachines.map((m, i) => <Line key={m.id} type="monotone" dataKey={m.code} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={1.5} dot={false} />)}
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

/* ---------------- REPORT 3: OPERATOR OUTPUT ---------------- */
function OperatorOutput({ production, team, machines, jobs }) {
  const rootRef = useRef(null);
  const [f, setF] = useState({ from: "", to: "", operator: "All", machine: "All", job: "All" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const teamMap = Object.fromEntries(team.map((t) => [t.id, t]));

  const filtered = production
    .filter((p) => inRange(p.date, f.from, f.to))
    .filter((p) => f.operator === "All" || p.operator_id === f.operator)
    .filter((p) => f.machine === "All" || p.machine_id === f.machine)
    .filter((p) => f.job === "All" || p.job_id === f.job);

  const rows = useMemo(() => {
    const map = {};
    filtered.forEach((p) => {
      const t = teamMap[p.operator_id];
      const key = p.operator_id || "unknown";
      const r = map[key] || (map[key] = { id: key, name: t?.name || "Unassigned", role: t?.role || "—", units: 0, hours: 0, effSum: 0, effN: 0, rework: 0 });
      r.units += num(p.actual_output);
      r.hours += num(p.actual_hours);
      r.rework += num(p.rework_scrap);
      if (num(p.planned_output) > 0) { r.effSum += (num(p.actual_output) / num(p.planned_output)) * 100; r.effN += 1; }
    });
    return Object.values(map).map((r) => ({ ...r, efficiency: r.effN ? r.effSum / r.effN : 0, reworkPct: (r.units + r.rework) ? (r.rework / (r.units + r.rework)) * 100 : 0 }));
  }, [filtered]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    return {
      top: [...rows].sort((a, b) => b.efficiency - a.efficiency)[0],
      rework: [...rows].sort((a, b) => b.rework - a.rework)[0],
      hours: [...rows].sort((a, b) => b.hours - a.hours)[0],
    };
  }, [rows]);

  const columns = [
    { key: "name", label: "Operator" },
    { key: "role", label: "Role" },
    { key: "units", label: "Units Produced", align: "right", value: (r) => r.units },
    { key: "hours", label: "Hours Worked", align: "right", value: (r) => r.hours.toFixed(1) },
    { key: "efficiency", label: "Efficiency %", align: "right", sortValue: (r) => r.efficiency, render: (r) => <span className={r.efficiency >= 80 ? "text-emerald-600 font-semibold" : r.efficiency >= 50 ? "text-amber-600 font-semibold" : "text-red-600 font-semibold"}>{pct(r.efficiency)}</span>, pdf: (r) => pct(r.efficiency), csv: (r) => r.efficiency.toFixed(1) },
    { key: "rework", label: "Rework Units", align: "right", value: (r) => r.rework },
    { key: "reworkPct", label: "Rework %", align: "right", sortValue: (r) => r.reworkPct, value: (r) => pct(r.reworkPct), csv: (r) => r.reworkPct.toFixed(1) },
  ];

  const chartData = [...rows].sort((a, b) => b.units - a.units).map((r) => ({ name: r.name, units: r.units }));

  return (
    <div ref={rootRef}>
      <ReportToolbar title="Operator Output & Efficiency" subtitle="Output, efficiency and rework per operator"
        onCsv={() => exportCsv("operator_output", columns, rows)}
        onPdf={async () => exportPdf({ title: "Operator Output & Efficiency", subtitle: `${rows.length} operators`, columns, rows, filename: "operator_output", charts: await captureCharts(rootRef.current) })}>
        <DateRange from={f.from} to={f.to} set={set} />
        <FilterField label="Operator"><Select value={f.operator} onValueChange={(v) => set("operator", v)}><SelectTrigger className="h-9 w-40" data-testid="filter-operator"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="All">All</SelectItem>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></FilterField>
        <FilterField label="Machine"><Select value={f.machine} onValueChange={(v) => set("machine", v)}><SelectTrigger className="h-9 w-40" data-testid="filter-machine"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="All">All</SelectItem>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.code}</SelectItem>)}</SelectContent></Select></FilterField>
        <FilterField label="Job"><Select value={f.job} onValueChange={(v) => set("job", v)}><SelectTrigger className="h-9 w-40" data-testid="filter-job"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="All">All</SelectItem>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.code}</SelectItem>)}</SelectContent></Select></FilterField>
      </ReportToolbar>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Top Performer" value={stats.top.name} sub={`Efficiency ${pct(stats.top.efficiency)}`} tone="green" />
          <StatCard label="Highest Rework" value={stats.rework.name} sub={`${stats.rework.rework} units`} tone="red" />
          <StatCard label="Most Hours Logged" value={stats.hours.name} sub={`${stats.hours.hours.toFixed(1)} hrs`} />
        </div>
      )}
      <SortableTable columns={columns} rows={rows} initialSort={{ key: "efficiency", dir: "desc" }} testId="r3" />

      <ChartCard title="Output Comparison Across Operators">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={0} angle={-15} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <Tooltip />
            <Bar dataKey="units" name="Units Produced" fill="#2563eb" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

/* ---------------- REPORT 4: COST VARIANCE BY JOB ---------------- */
function CostVariance({ costs, jobs }) {
  const rootRef = useRef(null);
  const [f, setF] = useState({ from: "", to: "", job: "All", category: "All" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const jobMap = Object.fromEntries(jobs.map((j) => [j.id, j]));
  const cats = ["All", ...new Set(costs.map((c) => c.cost_category).filter(Boolean))];

  const rows = costs
    .filter((c) => inRange(c.date, f.from, f.to))
    .filter((c) => f.job === "All" || c.job_id === f.job)
    .filter((c) => f.category === "All" || c.cost_category === f.category)
    .map((c) => {
      const planned = num(c.planned_cost), actual = num(c.actual_cost);
      const variance = actual - planned;
      const vpct = planned ? (variance / planned) * 100 : (actual > 0 ? 100 : 0);
      const cls = vpct > 10 ? "bg-red-50" : vpct >= 5 ? "bg-amber-50" : "bg-emerald-50";
      const job = jobMap[c.job_id];
      return { ...c, jobCode: job?.code || "—", jobName: job?.job_name || "—", planned, actual, variance, vpct, __rowClass: cls };
    });

  const catVar = {};
  rows.forEach((r) => catVar[r.cost_category] = (catVar[r.cost_category] || 0) + r.variance);
  const pieData = Object.entries(catVar).map(([k, v]) => ({ name: k, value: Math.abs(v), raw: v })).filter((d) => d.value > 0).sort((a, b) => b.value - a.value);

  const columns = [
    { key: "jobCode", label: "Job ID", render: (r) => <span className="font-mono font-medium">{r.jobCode}</span> },
    { key: "jobName", label: "Job Name" },
    { key: "cost_category", label: "Cost Category" },
    { key: "planned", label: "Planned", align: "right", value: (r) => rupee(r.planned), pdf: (r) => rupeePdf(r.planned), csv: (r) => r.planned },
    { key: "actual", label: "Actual", align: "right", value: (r) => rupee(r.actual), pdf: (r) => rupeePdf(r.actual), csv: (r) => r.actual },
    { key: "variance", label: "Variance", align: "right", sortValue: (r) => r.variance, render: (r) => <span className={r.variance > 0 ? "text-red-600 font-medium" : "text-emerald-600 font-medium"}>{rupee(r.variance)}</span>, pdf: (r) => rupeePdf(r.variance), csv: (r) => r.variance },
    { key: "vpct", label: "Variance %", align: "right", sortValue: (r) => r.vpct, render: (r) => <Badge variant="outline" className={r.vpct > 10 ? "bg-red-100 text-red-700 border-red-200" : r.vpct >= 5 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}>{pct(r.vpct)}</Badge>, pdf: (r) => pct(r.vpct), csv: (r) => r.vpct.toFixed(1) },
  ];

  return (
    <div ref={rootRef}>
      <ReportToolbar title="Cost Variance Analysis (by Job)" subtitle="Red > +10% · Yellow +5–10% · Green within ±5%"
        onCsv={() => exportCsv("cost_variance", columns, rows)}
        onPdf={async () => exportPdf({ title: "Cost Variance by Job", subtitle: `${rows.length} entries`, columns, rows, filename: "cost_variance", charts: await captureCharts(rootRef.current) })}>
        <DateRange from={f.from} to={f.to} set={set} />
        <FilterField label="Job"><Select value={f.job} onValueChange={(v) => set("job", v)}><SelectTrigger className="h-9 w-40" data-testid="filter-job"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="All">All</SelectItem>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.code}</SelectItem>)}</SelectContent></Select></FilterField>
        <FilterField label="Category"><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger className="h-9 w-44" data-testid="filter-category"><SelectValue /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></FilterField>
      </ReportToolbar>

      <SortableTable columns={columns} rows={rows} initialSort={{ key: "vpct", dir: "desc" }} testId="r4" />

      <ChartCard title="Variance Contribution by Cost Category">
        {pieData.length === 0 ? <div className="h-[280px] flex items-center justify-center text-slate-400">No variance data.</div> : (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e) => e.name}>
                {pieData.map((e, i) => <Cell key={i} fill={e.raw > 0 ? CHART_COLORS[3] : CHART_COLORS[1]} opacity={0.6 + 0.4 * (1 - i / Math.max(pieData.length, 1))} />)}
              </Pie>
              <Tooltip formatter={(v, n, p) => rupee(p.payload.raw)} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}

/* ---------------- REPORT 5: COST BREAKDOWN BY CATEGORY ---------------- */
function CostBreakdown({ costs }) {
  const rootRef = useRef(null);
  const [f, setF] = useState({ from: "", to: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const filtered = costs.filter((c) => inRange(c.date, f.from, f.to));

  const months = lastNMonths(12);
  const catSet = [...new Set(costs.map((c) => c.cost_category).filter(Boolean))];

  // over-budget flag: variance>0 for 3+ consecutive months
  const overBudget = {};
  catSet.forEach((cat) => {
    let run = 0, flag = false;
    months.forEach((mth) => {
      const ents = costs.filter((c) => c.cost_category === cat && (c.date || "").slice(0, 7) === mth);
      if (ents.length === 0) { run = 0; return; }
      const v = ents.reduce((s, c) => s + (num(c.actual_cost) - num(c.planned_cost)), 0);
      if (v > 0) { run += 1; if (run >= 3) flag = true; } else run = 0;
    });
    overBudget[cat] = flag;
  });

  const rows = catSet.map((cat) => {
    const ents = filtered.filter((c) => c.cost_category === cat);
    const planned = ents.reduce((s, c) => s + num(c.planned_cost), 0);
    const actual = ents.reduce((s, c) => s + num(c.actual_cost), 0);
    const variance = actual - planned;
    const vpct = planned ? (variance / planned) * 100 : (actual > 0 ? 100 : 0);
    return { id: cat, category: cat, planned, actual, variance, vpct, over: overBudget[cat] };
  }).filter((r) => r.planned || r.actual);

  const totalActual = rows.reduce((s, r) => s + r.actual, 0);
  const pieData = rows.filter((r) => r.actual > 0).map((r) => ({ name: r.category, value: r.actual }));

  const trend = months.map((mth) => {
    const row = { month: monthLabel(mth) };
    catSet.forEach((cat) => {
      row[cat] = costs.filter((c) => c.cost_category === cat && (c.date || "").slice(0, 7) === mth).reduce((s, c) => s + num(c.actual_cost), 0);
    });
    return row;
  });

  const columns = [
    { key: "category", label: "Cost Category", render: (r) => <span className="flex items-center gap-1.5">{r.category}{r.over && <Warning className="h-4 w-4 text-amber-500" weight="fill" title="Over budget 3+ consecutive months" />}</span>, csv: (r) => r.category + (r.over ? " (over-budget)" : ""), pdf: (r) => r.category + (r.over ? " *" : "") },
    { key: "planned", label: "Total Planned", align: "right", value: (r) => rupee(r.planned), pdf: (r) => rupeePdf(r.planned), csv: (r) => r.planned },
    { key: "actual", label: "Total Actual", align: "right", value: (r) => rupee(r.actual), pdf: (r) => rupeePdf(r.actual), csv: (r) => r.actual },
    { key: "variance", label: "Variance", align: "right", sortValue: (r) => r.variance, render: (r) => <span className={r.variance > 0 ? "text-red-600 font-medium" : "text-emerald-600 font-medium"}>{rupee(r.variance)}</span>, pdf: (r) => rupeePdf(r.variance), csv: (r) => r.variance },
    { key: "vpct", label: "Variance %", align: "right", sortValue: (r) => r.vpct, value: (r) => pct(r.vpct), csv: (r) => r.vpct.toFixed(1) },
  ];

  return (
    <div ref={rootRef}>
      <ReportToolbar title="Cost Breakdown by Category" subtitle="All jobs combined · ⚠ = over budget 3+ consecutive months"
        onCsv={() => exportCsv("cost_breakdown", columns, rows)}
        onPdf={async () => exportPdf({ title: "Cost Breakdown by Category", subtitle: `Total actual ${rupeePdf(totalActual)}`, columns, rows, filename: "cost_breakdown", charts: await captureCharts(rootRef.current) })}>
        <DateRange from={f.from} to={f.to} set={set} />
      </ReportToolbar>

      <SortableTable columns={columns} rows={rows} initialSort={{ key: "actual", dir: "desc" }} testId="r5" />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ChartCard title="Actual Cost Share by Category">
          {pieData.length === 0 ? <div className="h-[280px] flex items-center justify-center text-slate-400">No cost data.</div> : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={45}>
                  {pieData.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => rupee(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Monthly Cost by Category — Last 12 Months">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" />
              <Tooltip formatter={(v) => rupee(v)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {catSet.map((cat, i) => <Line key={cat} type="monotone" dataKey={cat} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={1.5} dot={false} />)}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

/* ---------------- CONTAINER ---------------- */
const TABS = [
  { key: "profitability", label: "Job Profitability", icon: ChartBar },
  { key: "utilization", label: "Machine Utilization", icon: Gear },
  { key: "operator", label: "Operator Output", icon: UsersThree },
  { key: "efficiency", label: "Operator Efficiency", icon: Trophy },
  { key: "variance", label: "Cost Variance", icon: Scales },
  { key: "breakdown", label: "Cost Breakdown", icon: ChartPieSlice },
  { key: "idle", label: "Idle Time", icon: Clock, adminOnly: true },
];

export default function Reports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user?.role);
  const [tab, setTab] = useState("profitability");
  const jobs = useList("jobs");
  const costs = useList("costs");
  const external = useList("external");
  const machines = useList("machines");
  const utilization = useList("utilization");
  const production = useList("production");
  const team = useList("team");
  const operations = useList("operations");

  return (
    <div className="relative">
      {/* faint brand watermark */}
      <div className="pointer-events-none fixed inset-0 flex items-center justify-center z-0" aria-hidden="true">
        <img src={LOGO_URL} alt="" className="w-[520px] max-w-[80%] opacity-[0.04] grayscale" />
      </div>

      <div className="relative z-10">
        <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="reports-back">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <div className="flex items-center gap-3 mb-5">
          <img src={LOGO_URL} alt="Apt Enterprises" className="h-9 w-auto" data-testid="reports-logo" />
          <div className="border-l border-slate-200 pl-3">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Reports</h1>
            <p className="text-xs text-slate-500">Apt Enterprises · Tool Room Workshop</p>
          </div>
        </div>

        {/* tab bar */}
        <div className="flex gap-1 overflow-x-auto border-b border-slate-200 mb-6 -mx-1 px-1">
          {TABS.filter((t) => !t.adminOnly || admin).map((t) => {
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`report-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${active ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "profitability" && <JobProfitability jobs={jobs} costs={costs} external={external} />}
        {tab === "utilization" && <MachineUtilization machines={machines} utilization={utilization} />}
        {tab === "operator" && <OperatorOutput production={production} team={team} machines={machines} jobs={jobs} />}
        {tab === "efficiency" && <OperatorEfficiency production={production} team={team} jobs={jobs} />}
        {tab === "variance" && <CostVariance costs={costs} jobs={jobs} />}
        {tab === "breakdown" && <CostBreakdown costs={costs} />}
        {tab === "idle" && admin && <IdleTimeReport team={team} />}
      </div>
    </div>
  );
}
